import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Flex, LoadingIndicator, Select, Text, toast } from "@jsq/ds/core";
import { CustomReportRecipientObjectType } from "~/graphql/supergraphEnums";
import {
  useGetReportTemplatesByArenaQuery,
  useGetReportTemplateQuery,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import {
  missingDatesPrompt,
  PreviewDateParameterFields,
  usePreviewDateParameters,
} from "../../dateParameters";
import { useTemplatePreviewJob } from "../../ReportTemplateViewer/hooks/useTemplatePreviewJob";
import { useArenaOptions } from "../hooks/useArenaOptions";
import { usePreviewEntities } from "../hooks/usePreviewEntities";
import { usePreviewPositions } from "../hooks/usePreviewPositions";
import { usePreviewTransactionBatches } from "../hooks/usePreviewTransactionBatches";
import { fetchTemplateFile } from "../services/carboneRenderService";
import * as styles from "../styles_vanilla.css";
import { getLatestVersion, isTransactionBasedReport, type ReportType } from "../types";
import { ArenaSelect } from "./ArenaSelect";
import { PreviewPanel, PreviewPlaceholder } from "./PreviewPanel";
import type { TemplateFileData } from "./TemplateCreation";

export type DuplicateTemplatePrefillData = {
  templateType: ReportType;
  dataPackageId: string;
  queryId: string;
};

export type DuplicateTemplateStepProps = {
  arenaId: number | null;
  arenaName: string;
  initialTemplateId?: number | null;
  onNext: (
    templateFileData: TemplateFileData | null,
    prefillData: DuplicateTemplatePrefillData,
  ) => void;
};

export const DuplicateTemplateStep = ({
  arenaId,
  arenaName,
  initialTemplateId,
  onNext,
}: DuplicateTemplateStepProps) => {
  const isSystemTemplate = arenaId === null;
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    initialTemplateId ?? null,
  );
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);
  const [templateFileData, setTemplateFileData] = useState<TemplateFileData | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { arenas, arenasLoading, selectedArenaId, setSelectedArenaId } =
    useArenaOptions(!isSystemTemplate);

  const effectiveArenaId = isSystemTemplate
    ? selectedArenaId != null
      ? toGlobalId("ArenaObjectType", selectedArenaId)
      : ""
    : toGlobalId("ArenaObjectType", arenaId);

  const { entities, loading: entitiesLoading } = usePreviewEntities(effectiveArenaId);

  const { data: systemTemplatesData } = useGetReportTemplatesByArenaQuery({
    variables: { arenaId: null },
  });

  const { data: arenaTemplatesData } = useGetReportTemplatesByArenaQuery({
    variables: { arenaId: arenaId ?? 0 },
    skip: arenaId === null,
  });

  const { data: selectedTemplateData, loading: templateDetailsLoading } =
    useGetReportTemplateQuery({
      variables: { id: selectedTemplateId ?? 0 },
      skip: selectedTemplateId === null,
      fetchPolicy: "cache-and-network",
    });

  const systemTemplateOptions = useMemo(
    () =>
      (systemTemplatesData?.reportTemplatesByArena ?? [])
        .filter((t) => t.isActive)
        .map((t) => ({
          id: t.id,
          name: t.name,
        })),
    [systemTemplatesData],
  );

  const arenaTemplateOptions = useMemo(
    () =>
      (arenaTemplatesData?.reportTemplatesByArena ?? [])
        .filter((t) => t.isActive)
        .map((t) => ({
          id: t.id,
          name: t.name,
        })),
    [arenaTemplatesData],
  );

  const templateDataMatchesSelection =
    selectedTemplateId != null &&
    selectedTemplateData?.reportTemplate?.id === selectedTemplateId;

  const effectiveVersion = useMemo(() => {
    if (!templateDataMatchesSelection) return null;
    const tmpl = selectedTemplateData?.reportTemplate;
    return getLatestVersion(tmpl?.currentVersion, tmpl?.versions);
  }, [selectedTemplateData, templateDataMatchesSelection]);

  const downloadUrl = effectiveVersion?.downloadUrl ?? null;
  const hasVersion = effectiveVersion != null;

  const needsArena = isSystemTemplate && selectedArenaId == null;

  const template = templateDataMatchesSelection ? selectedTemplateData?.reportTemplate : null;
  const transactionBased = isTransactionBasedReport(template?.reportType ?? "");

  useEffect(() => {
    if (!transactionBased && !entitiesLoading && entities.length > 0 && !selectedEntity) {
      setSelectedEntity(entities[0].id);
    }
  }, [transactionBased, entities, entitiesLoading, selectedEntity]);

  useEffect(() => {
    if (!downloadUrl || !selectedTemplateId) {
      return;
    }

    let cancelled = false;
    setIsDownloading(true);
    setDownloadError(null);

    fetchTemplateFile(downloadUrl)
      .then((data) => {
        if (!cancelled) {
          setTemplateFileData(data);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTemplateFileData(null);
          setDownloadError(err instanceof Error ? err.message : "Failed to download template");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsDownloading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [downloadUrl, selectedTemplateId]);

  const entityIdForAccounts = transactionBased ? (selectedEntityIds[0] ?? "") : selectedEntity;
  const { accounts, loading: accountsLoading } = usePreviewPositions(entityIdForAccounts);

  const arenaDbId = isSystemTemplate ? selectedArenaId : arenaId;
  const { batches: transactionBatches, loading: transactionBatchesLoading } =
    usePreviewTransactionBatches(
      selectedEntityIds,
      arenaDbId,
      effectiveVersion?.queryId ?? null,
    );

  const recipientAccountDbId = selectedAccount ? toDbId(selectedAccount) : null;

  const glTransactionIds = transactionBased ? selectedTransactionIds.filter(Boolean) : null;

  const hasEntities = transactionBased ? selectedEntityIds.length > 0 : Boolean(selectedEntity);
  const hasTransactions = selectedTransactionIds.length > 0;

  const dateParameters = usePreviewDateParameters({
    queryId: effectiveVersion?.queryId ?? null,
    arenaId: template?.dataPackage?.arenaId ?? null,
  });

  const { pdfUrl, isRendering, renderError } = useTemplatePreviewJob({
    reportTemplateId: selectedTemplateId,
    reportTemplateVersionId: effectiveVersion?.id ?? null,
    recipientObjectType: CustomReportRecipientObjectType.Account,
    recipientObjectId: recipientAccountDbId,
    arenaGlobalId: effectiveArenaId,
    parameters: dateParameters.parameters,
    glTransactionIds,
    skip:
      needsArena ||
      !hasVersion ||
      (transactionBased
        ? !hasEntities || !hasTransactions || !selectedAccount
        : !selectedEntity) ||
      !dateParameters.isResolved ||
      !dateParameters.isComplete,
  });

  const handleTemplateSelect = useCallback((value: string | number | string[] | number[]) => {
    const raw = Array.isArray(value) ? value[0] : value;
    if (raw === undefined || raw === "") {
      setSelectedTemplateId(null);
    } else {
      const id = Number(raw);
      setSelectedTemplateId(Number.isNaN(id) ? null : id);
    }
    setTemplateFileData(null);
    setDownloadError(null);
    // A different template invalidates the entity/account/transaction selections.
    setSelectedTransactionIds([]);
    setSelectedEntityIds([]);
    setSelectedAccount("");
  }, []);

  const handleArenaChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      if (raw === undefined || raw === "") {
        setSelectedArenaId(null);
      } else {
        const id = Number(raw);
        setSelectedArenaId(Number.isNaN(id) ? null : id);
      }
      setSelectedEntity("");
      setSelectedAccount("");
      setSelectedEntityIds([]);
      setSelectedTransactionIds([]);
    },
    [setSelectedArenaId],
  );

  const handleEntitiesChange = useCallback((value: string | number | string[] | number[]) => {
    // The transaction and account lists are scoped to the entity selection.
    setSelectedTransactionIds([]);
    setSelectedAccount("");
    setSelectedEntityIds(Array.isArray(value) ? value.map(String) : [String(value)]);
  }, []);

  const handleTransactionsChange = useCallback(
    (value: string | number | string[] | number[]) => {
      setSelectedTransactionIds(Array.isArray(value) ? value.map(String) : [String(value)]);
    },
    [],
  );

  const handleDownload = useCallback(() => {
    if (!templateFileData?.dataURI) return;
    const rawName = selectedTemplateData?.reportTemplate?.name ?? "template";
    const sanitizedName = rawName.replace(/[/\\:*?"<>|]/g, "_");
    const date = new Date().toISOString().slice(0, 10);
    const link = document.createElement("a");
    link.href = templateFileData.dataURI;
    link.download = `${sanitizedName}-${date}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [templateFileData, selectedTemplateData]);

  const handleNext = useCallback(() => {
    const tmpl = selectedTemplateData?.reportTemplate;
    if (tmpl == null || tmpl.reportType == null) {
      toast.error("This template is missing a report type. Choose another template.");
      return;
    }
    onNext(templateFileData, {
      templateType: tmpl.reportType,
      dataPackageId: tmpl.dataPackage != null ? String(tmpl.dataPackage.id) : "",
      queryId: effectiveVersion?.queryId != null ? String(effectiveVersion.queryId) : "",
    });
  }, [effectiveVersion, templateFileData, selectedTemplateData, onNext]);

  const placeholder = (() => {
    if (selectedTemplateId === null) {
      return (
        <PreviewPlaceholder
          title="Template preview"
          description="Select a template to see a preview here"
        />
      );
    }
    if (needsArena) {
      return (
        <PreviewPlaceholder title="Template preview" description="Select an arena to preview" />
      );
    }
    if (!templateDetailsLoading && !hasVersion) {
      return (
        <PreviewPlaceholder
          title="No template file"
          description="This template has no uploaded version to preview."
        />
      );
    }
    if (!hasEntities) {
      return (
        <PreviewPlaceholder
          title="Select an entity to preview"
          description="Choose an entity to generate a preview with sample data."
        />
      );
    }
    if (transactionBased && !hasTransactions) {
      return (
        <PreviewPlaceholder
          title="Select transactions to preview"
          description="Choose one or more transactions to generate a preview."
        />
      );
    }
    if (!selectedAccount) {
      return (
        <PreviewPlaceholder
          title="Select an account to preview"
          description="Choose an account to generate a preview with sample data."
        />
      );
    }
    if (dateParameters.isResolved && !dateParameters.isComplete) {
      const prompt = missingDatesPrompt(dateParameters.requiredParameters);
      return <PreviewPlaceholder title={prompt.title} description={prompt.description} />;
    }
    return <LoadingIndicator />;
  })();

  return (
    <Flex className={styles.previewDuplicateWrapper}>
      <Flex flexDirection="column" gap={4} className={styles.previewFormSidebar}>
        <Text variant="strong-md">Select a template</Text>
        <Text variant="regular-sm">
          Preview and download a template to make changes. You will be prompted to upload the
          revised file later in the process.
        </Text>

        <Flex
          flexDirection="column"
          gap={SPACING.MEDIUM}
          className={styles.previewSelectsContainer}
        >
          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Select
              label="Duplicate from"
              placeholder="Select template"
              full
              value={selectedTemplateId ? String(selectedTemplateId) : ""}
              onChange={handleTemplateSelect}
              dividerPosition="all"
            >
              {[
                ...(arenaId != null
                  ? [
                      <Select.Section key="arena" name={`${arenaName} templates`}>
                        {arenaTemplateOptions.map((t) => (
                          <Select.Option key={t.id} value={String(t.id)}>
                            {t.name}
                          </Select.Option>
                        ))}
                      </Select.Section>,
                    ]
                  : []),
                <Select.Section key="system" name="System templates">
                  {systemTemplateOptions.map((t) => (
                    <Select.Option key={t.id} value={String(t.id)}>
                      {t.name}
                    </Select.Option>
                  ))}
                </Select.Section>,
              ]}
            </Select>
            <Button
              displayAsLink
              icon="download"
              onClick={handleDownload}
              disabled={!templateFileData || isDownloading}
            >
              Download template
            </Button>
            {downloadError && (
              <Text variant="secondary-sm" color="danger" data-testid="template-download-error">
                {downloadError}
              </Text>
            )}
          </Flex>

          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Text variant="h4" marginBottom="md">
              Preview settings
            </Text>

            {isSystemTemplate && (
              <ArenaSelect
                arenas={arenas}
                arenasLoading={arenasLoading}
                selectedArenaId={selectedArenaId}
                onChange={handleArenaChange}
              />
            )}

            {transactionBased ? (
              <>
                <Select
                  label="Entity"
                  placeholder="Select entities"
                  full
                  multiple
                  matchFromStart={false}
                  disabled={entitiesLoading || (isSystemTemplate && selectedArenaId == null)}
                  value={selectedEntityIds}
                  onChange={handleEntitiesChange}
                >
                  <Select.Section>
                    {entities.map((e) => (
                      <Select.Option key={e.id} value={e.id}>
                        {e.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                <Select
                  label="Transactions"
                  placeholder="Select transactions"
                  full
                  multiple
                  disabled={transactionBatchesLoading || selectedEntityIds.length === 0}
                  value={selectedTransactionIds}
                  onChange={handleTransactionsChange}
                >
                  <Select.Section>
                    {transactionBatches.map((batch) => (
                      <Select.Option key={batch.id} value={batch.id}>
                        {batch.label}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                <Select
                  label="Account"
                  placeholder="Select account"
                  full
                  disabled={accountsLoading || selectedEntityIds.length === 0}
                  value={selectedAccount}
                  onChange={(v) => setSelectedAccount(String(v))}
                >
                  <Select.Section>
                    {accounts.map((account) => (
                      <Select.Option key={account.id} value={account.id}>
                        {account.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
              </>
            ) : (
              <>
                <Select
                  label="Entity"
                  full
                  placeholder="Select entity"
                  matchFromStart={false}
                  disabled={entitiesLoading || (isSystemTemplate && selectedArenaId == null)}
                  value={selectedEntity}
                  onChange={(v) => {
                    setSelectedEntity(String(v));
                    setSelectedAccount("");
                  }}
                >
                  <Select.Section>
                    {entities.map((e) => (
                      <Select.Option key={e.id} value={e.id}>
                        {e.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                <Select
                  label="Account"
                  full
                  placeholder="Select account"
                  disabled={!selectedEntity || accountsLoading}
                  value={selectedAccount}
                  onChange={(v) => setSelectedAccount(String(v))}
                >
                  <Select.Section>
                    {accounts.map((account) => (
                      <Select.Option key={account.id} value={account.id}>
                        {account.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
              </>
            )}
            <PreviewDateParameterFields dateParameters={dateParameters} />
          </Flex>
        </Flex>

        <Flex justifyContent="flex-end" style={{ marginTop: SPACING.LARGE }}>
          <Button variant="primary" onClick={handleNext} disabled={!selectedTemplateId}>
            Next
          </Button>
        </Flex>
      </Flex>

      <PreviewPanel
        isLoading={!needsArena && templateDetailsLoading}
        pdfUrl={pdfUrl}
        isRendering={isRendering}
        error={dateParameters.loadError ?? renderError}
        placeholder={placeholder}
      />
    </Flex>
  );
};
